const NO_SCROLL_CLASS = 'scroll-carousel--no-scroll';

/**
 * Shared horizontal scroll carousel with pagination dots.
 * Pagination reflects viewport-width "pages"; clicking a dot scrolls, scrolling updates the active dot.
 *
 * @param {Object} config
 * @param {string} config.containerSelector - Section/wrapper to find (e.g. '.hands-on-expertise')
 * @param {string} config.trackSelector - Scrollable element (has overflow-x)
 * @param {string} [config.viewportSelector] - Element to measure viewport width; defaults to track
 * @param {string} config.dotsContainerSelector - Container for pagination dots (content will be replaced)
 * @param {string} config.dotClassName - Class name for each dot button
 * @param {boolean} [config.hidePaginationWhenSinglePage] - Hide dots when all content fits (no scroll)
 * @param {boolean} [config.centerWhenNoScroll] - Add class to container when no scroll (for CSS centering)
 */
export function initScrollCarousels(config) {
    const {
        containerSelector,
        trackSelector,
        viewportSelector = null,
        dotsContainerSelector,
        dotClassName,
        hidePaginationWhenSinglePage = false,
        centerWhenNoScroll = false,
    } = config;

    const containers = document.querySelectorAll(containerSelector);
    containers.forEach((container) => {
        const track = container.querySelector(trackSelector);
        const viewport = viewportSelector
            ? container.querySelector(viewportSelector)
            : track;
        const dotsEl = container.querySelector(dotsContainerSelector);

        if (!track || !viewport || !dotsEl) return;

        const getNumPages = () => {
            const viewportWidth = viewport.clientWidth;
            const scrollWidth = track.scrollWidth;
            if (scrollWidth <= viewportWidth) return 1;
            return Math.ceil(scrollWidth / viewportWidth);
        };

        const updateNoScrollState = (numPages) => {
            const noScroll = numPages <= 1;
            if (hidePaginationWhenSinglePage) {
                dotsEl.style.display = noScroll ? 'none' : '';
            }
            if (centerWhenNoScroll) {
                container.classList.toggle(NO_SCROLL_CLASS, noScroll);
            }
        };

        const renderDots = (numPages) => {
            dotsEl.innerHTML = '';
            for (let i = 0; i < numPages; i++) {
                const btn = document.createElement('button');
                btn.type = 'button';
                btn.className = dotClassName + (i === 0 ? ' active' : '');
                btn.dataset.index = String(i);
                btn.setAttribute('aria-label', `Go to page ${i + 1} of ${numPages}`);
                btn.addEventListener('click', () => goToPage(i));
                dotsEl.appendChild(btn);
            }
            updateNoScrollState(numPages);
        };

        const goToPage = (index) => {
            const viewportWidth = viewport.clientWidth;
            const maxScroll = track.scrollWidth - viewportWidth;
            if (maxScroll <= 0) return;
            const numPages = dotsEl.children.length;
            const left = numPages > 1 ? (index / (numPages - 1)) * maxScroll : 0;
            track.scrollTo({ left, behavior: 'smooth' });
        };

        const updateActiveDot = () => {
            const viewportWidth = viewport.clientWidth;
            const maxScroll = track.scrollWidth - viewportWidth;
            const dots = dotsEl.querySelectorAll('.' + dotClassName);
            if (dots.length === 0 || maxScroll <= 0) return;
            const numPages = dots.length;
            const index = numPages > 1
                ? Math.max(0, Math.min(numPages - 1, Math.round((track.scrollLeft / maxScroll) * (numPages - 1))))
                : 0;
            dots.forEach((dot, i) => dot.classList.toggle('active', i === index));
        };

        const numPages = getNumPages();
        renderDots(numPages);
        track.addEventListener('scroll', updateActiveDot);
        updateActiveDot();

        window.addEventListener('resize', () => {
            const next = getNumPages();
            const currentCount = dotsEl.children.length;
            if (next !== currentCount) {
                renderDots(next);
            } else {
                updateNoScrollState(next);
            }
            updateActiveDot();
        });
    });
}
